import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import MapIcon from '@mui/icons-material/Map';
import '@fortawesome/fontawesome-free/css/all.min.css';

const UnsupportedFileIcon = ({ documentName, documentType }) => {
  const fileName = (documentName || '').toLowerCase().split(/[?#]/)[0];
  const mimeType = (documentType || '').toLowerCase();

  if (
    /\.(xlsx?|xlsm)$/.test(fileName) ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('ms-excel')
  ) {
    return (
      <i
        className="fa-solid fa-file-excel"
        style={{ fontSize: '64px', color: '#217346' }}
        aria-label="Excel file"
      />
    );
  }

  if (fileName.endsWith('.kmz') || mimeType.includes('vnd.google-earth.kmz')) {
    return <MapIcon sx={{ fontSize: 64, color: '#4285F4' }} aria-label="KMZ file" />;
  }

  if (fileName.endsWith('.msg') || mimeType.includes('vnd.ms-outlook')) {
    return <EmailIcon sx={{ fontSize: 64, color: '#2563EB' }} aria-label="MSG file" />;
  }

  return <DescriptionIcon sx={{ fontSize: 64, color: '#667085' }} />;
};

const DocumentPreview = ({ documentUrl, documentName, documentType, isLoading, downloadUrl }) => {
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const iframeRef = useRef(null);
  const loadTimeoutRef = useRef(null);

  // Note: Excel/CSV files are now converted to PDF on the backend for preview
  // So we don't need to check for Excel/CSV separately - they'll be treated as PDFs

  // Reset error state and force reload when document URL changes
  useEffect(() => {
    setLoadError(false);
    setIframeLoaded(false);
    setIsIframeLoading(true); // Show loader when URL changes
    setIsImageLoading(true); // Show loader for images when URL changes
    setReloadKey((prev) => prev + 1); // Force iframe to reload

    // Clear any existing timeout
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }

    // Force iframe to reload if it exists
    if (iframeRef.current && documentUrl) {
      // Clear src first, then set it to force reload
      iframeRef.current.src = '';
      setTimeout(() => {
        if (iframeRef.current && documentUrl) {
          iframeRef.current.src = documentUrl;
        }
      }, 10);
    }

    // Set a timeout to detect if iframe doesn't load
    if (documentUrl) {
      loadTimeoutRef.current = setTimeout(() => {
        setIframeLoaded((loaded) => {
          if (!loaded) {
            setLoadError(true);
            setIsIframeLoading(false);
          }
          return loaded;
        });
      }, 5000); // 5 second timeout
    }
  }, [documentUrl]); // Removed iframeLoaded from dependencies to prevent blinking

  if (isLoading) {
    return (
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid #E8E9E9',
          borderRadius: '8px',
          padding: '32px',
          minHeight: '900px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'sticky',
          top: '24px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!documentUrl) {
    return (
      <Box
        sx={{
          background: '#FFFFFF',
          border: '1px solid #E8E9E9',
          borderRadius: '8px',
          padding: '32px',
          minHeight: '900px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'sticky',
          top: '24px',
        }}
      >
        <PictureAsPdfIcon sx={{ fontSize: 64, color: '#DC2626', mb: 2 }} />
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 400,
            color: '#667085',
          }}
        >
          Select a document to preview
        </Typography>
      </Box>
    );
  }

  const isPDF = documentType === 'application/pdf' || documentName?.toLowerCase().endsWith('.pdf');
  const isImage =
    documentType?.startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(documentName || '');

  const handleIframeError = () => {
    setLoadError(true);
  };

  const handleDownload = () => {
    // Use downloadUrl if provided (original file), otherwise use documentUrl (PDF preview)
    const urlToDownload = downloadUrl || documentUrl;
    if (urlToDownload) {
      window.open(urlToDownload, '_blank');
    }
  };

  return (
    <Box
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E8E9E9',
        borderRadius: '8px',
        padding: '32px',
        minHeight: '900px',
        position: 'sticky',
        top: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontFamily: 'Inter',
            fontWeight: 600,
            color: '#192126',
          }}
        >
          {documentName || 'Document Preview'}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{
            textTransform: 'none',
            borderColor: '#E8E9E9',
            color: '#192126',
            '&:hover': {
              borderColor: '#BD1D23',
              backgroundColor: 'rgba(189, 29, 35, 0.04)',
            },
          }}
        >
          Download
        </Button>
      </Box>

      <Box
        sx={{
          width: '100%',
          height: 'calc(900px - 100px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #E8E9E9',
          borderRadius: '8px',
          overflow: 'hidden',
          backgroundColor: '#F8F9FA',
          position: 'relative',
        }}
      >
        {loadError ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              p: 4,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 64, color: '#DC2626' }} />
            <Typography
              sx={{
                fontSize: '16px',
                fontFamily: 'Inter',
                fontWeight: 600,
                color: '#192126',
              }}
            >
              Unable to load document
            </Typography>
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#667085',
                textAlign: 'center',
              }}
            >
              The document could not be displayed in the preview. Please download it to view.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: 'none',
                backgroundColor: '#BD1D23',
                '&:hover': {
                  backgroundColor: '#8B1519',
                },
              }}
            >
              Download Document
            </Button>
          </Box>
        ) : isPDF ? (
          <>
            {loadError ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  p: 4,
                  width: '100%',
                  height: '100%',
                }}
              >
                <ErrorOutlineIcon sx={{ fontSize: 64, color: '#DC2626' }} />
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    color: '#192126',
                  }}
                >
                  Unable to load document
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    color: '#667085',
                    textAlign: 'center',
                  }}
                >
                  The document could not be displayed. Please download it to view.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: '#BD1D23',
                    '&:hover': {
                      backgroundColor: '#8B1519',
                    },
                  }}
                >
                  Download Document
                </Button>
              </Box>
            ) : (
              <>
                <iframe
                  key={`pdf-${reloadKey}-${documentUrl}`} // Force re-render when URL changes
                  ref={iframeRef}
                  src={documentUrl || ''} // Use documentUrl directly (can be blob URL or regular URL)
                  title={documentName}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    display: 'block',
                  }}
                  onLoad={(e) => {
                    // Clear timeout and mark as loaded
                    if (loadTimeoutRef.current) {
                      clearTimeout(loadTimeoutRef.current);
                      loadTimeoutRef.current = null;
                    }
                    setIframeLoaded(true);
                    setIsIframeLoading(false); // Hide loader when iframe loads
                    setLoadError(false);
                  }}
                  onError={(e) => {
                    // Handle explicit iframe errors
                    if (loadTimeoutRef.current) {
                      clearTimeout(loadTimeoutRef.current);
                      loadTimeoutRef.current = null;
                    }
                    setLoadError(true);
                    setIsIframeLoading(false); // Hide loader on error
                    setIframeLoaded(false);
                  }}
                />
                {/* Loader overlay - shows while iframe is loading */}
                {isIframeLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                )}
              </>
            )}
          </>
        ) : isImage ? (
          <>
            <img
              src={documentUrl}
              alt={documentName}
              onError={(e) => {
                handleIframeError();
                setIsImageLoading(false);
              }}
              onLoad={() => {
                setIsImageLoading(false);
              }}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
            />
            {/* Loader overlay for images */}
            {isImageLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  zIndex: 10,
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <UnsupportedFileIcon documentName={documentName} documentType={documentType} />
            <Typography
              sx={{
                fontSize: '14px',
                fontFamily: 'Inter',
                fontWeight: 400,
                color: '#667085',
              }}
            >
              Preview not available for this file type
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{
                textTransform: 'none',
                backgroundColor: '#BD1D23',
                '&:hover': {
                  backgroundColor: '#8B1519',
                },
              }}
            >
              Download to view
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentPreview;
